import type { MetricSeries, MetricSeriesResponse } from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { getMetricSeries } from '../api/metrics.js';
import { formatPercent } from '../lib/format.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';
import { CpuIcon, GaugeIcon, MemoryIcon } from '../ui/icons.js';
import { StatIcon } from '../ui/StatIcon.js';
import { SelectField } from '../ui/Field.js';
import { MetricChart } from './MetricChart.js';

/**
 * Processor, load and memory on a server's page (P11.10), from the `system`
 * module's metrics. Charts only: nothing alerts on them.
 *
 * A server whose agent predates the module has no readings; the card says so
 * rather than drawing empty axes.
 */

const RANGES = [
  { value: '1', label: 'Last hour' },
  { value: '6', label: 'Last 6 hours' },
  { value: '24', label: 'Last day' },
  { value: '168', label: 'Last week' },
] as const;

const NO_LABEL = (): string => 'This server';

export function SystemCard({
  serverId,
  timezone,
}: {
  serverId: string;
  timezone: string;
}): ReactNode {
  const [hours, setHours] = useState('6');
  const from = new Date(Date.now() - Number(hours) * 3_600_000).toISOString();

  const metric = (name: string) => ({
    queryKey: ['metrics', serverId, name, hours],
    queryFn: () => getMetricSeries({ serverId, name, from }),
  });
  const cpu = useQuery(metric('cpu_used_pct'));
  const memory = useQuery(metric('memory_used_pct'));
  const load = useQuery(metric('load_avg_1m'));
  const swap = useQuery(metric('swap_used_pct'));

  const failed = cpu.isError || memory.isError || load.isError;
  const pending = cpu.isPending || memory.isPending || load.isPending;
  const hasData = [cpu, memory, load].some((each) => (each.data?.series.length ?? 0) > 0);

  return (
    <Card
      title="Processor and memory"
      description="How busy the server is, and how much memory is in use. Cached memory is not counted as used."
      aside={
        <SelectField
          label="Range"
          value={hours}
          onChange={(event) => {
            setHours(event.target.value);
          }}
        >
          {RANGES.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </SelectField>
      }
    >
      {failed && (
        <Alert tone="danger" title="Could not load the history">
          Try again in a moment.
        </Alert>
      )}
      {pending && (
        <div className="skeleton skeleton-block" aria-busy="true" aria-label="Loading charts" />
      )}
      {!pending && !failed && !hasData && (
        <p className="muted text-sm">
          No readings yet. The agent reports these once it is updated to a version with the system
          module, within a few minutes of restarting.
        </p>
      )}
      {hasData && (
        <div className="stack">
          <dl className="stat-tiles">
            <Headline
              icon={CpuIcon}
              label="Processor"
              value={latest(cpu.data?.series)}
              format={percent}
            />
            <Headline
              icon={MemoryIcon}
              label="Memory"
              value={latest(memory.data?.series)}
              format={percent}
            />
            <Headline
              icon={GaugeIcon}
              label="Load (1 min)"
              value={latest(load.data?.series)}
              format={twoDecimals}
            />
            <Headline
              icon={MemoryIcon}
              label="Swap"
              value={latest(swap.data?.series)}
              format={percent}
            />
          </dl>
          <div className="traffic-grid">
            <ChartBox
              title="Processor used"
              query={cpu.data}
              format={percent}
              pinned
              timezone={timezone}
            />
            <ChartBox
              title="Memory used"
              query={memory.data}
              format={percent}
              pinned
              timezone={timezone}
            />
            <ChartBox
              title="Load average (1 minute)"
              query={load.data}
              format={twoDecimals}
              timezone={timezone}
            />
            {swap.data !== undefined && swap.data.series.length > 0 && (
              <ChartBox
                title="Swap used"
                query={swap.data}
                format={percent}
                pinned
                timezone={timezone}
              />
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

const percent = (value: number): string => formatPercent(value);
const twoDecimals = (value: number): string => value.toFixed(2);

/** The newest bucket's average of the first line, for the headline figure. */
function latest(series: MetricSeries[] | undefined): number | undefined {
  return series?.[0]?.points.at(-1)?.avg;
}

function Headline({
  label,
  value,
  format,
  icon,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  value: number | undefined;
  format: (value: number) => string;
}): ReactNode {
  return (
    <div className="stat-tile">
      <StatIcon icon={icon} />
      <dt>{label}</dt>
      <dd>{value === undefined ? '—' : format(value)}</dd>
    </div>
  );
}

function ChartBox({
  title,
  query,
  format,
  pinned = false,
  timezone,
}: {
  title: string;
  query: MetricSeriesResponse | undefined;
  format: (value: number) => string;
  pinned?: boolean;
  timezone: string;
}): ReactNode {
  if (query === undefined) return null;
  return (
    <section>
      <h3 className="text-sm">{title}</h3>
      <MetricChart
        title={title}
        series={query.series}
        from={query.from}
        to={query.to}
        bucketSeconds={query.bucketSeconds}
        format={format}
        seriesLabel={NO_LABEL}
        {...(pinned ? { yMin: 0, yMax: 100 } : { yMin: 0 })}
        timezone={timezone}
        height={150}
        emptyText="No readings in this range."
      />
    </section>
  );
}
